import { useTranslation } from "react-i18next";
import { User, Palette, Globe, Clock, Sun, MoonStar, Monitor } from "lucide-react";
import { useAppSelector, useAppDispatch } from "../app/hooks";
import {
  selectCurrentRegionalCode,
  selectAvailableVariants,
  changeLanguage,
} from "../features/localization/localizationSlice";
import { REGIONAL_VARIANTS } from "../features/localization/utils/regionalDetection";
import { useTheme } from "../features/theme/useTheme";
import { ThemeMode } from "../features/theme/themeSlice";
import LeftMenu from "../components/LeftMenu";
import ProfileNameEditor from "../components/settings/ProfileNameEditor";
import LicenceNumberEditor from "../components/settings/LicenceNumberEditor";
import DeleteAccountSection from "../components/settings/DeleteAccountSection";
import Breadcrumbs from "../components/Breadcrumbs";
import SegmentedToggle from "../components/SegmentedToggle";
import SchedulePreferencesSection from "../features/instructor-preferences/SchedulePreferencesSection";
import LegalConsentSettings from "../features/instructor-consent/LegalConsentSettings";
import OrganizationSettingsLink from "../features/organization/components/OrganizationSettingsLink";
import ProgrammesSettingsLink from "../features/training/components/ProgrammesSettingsLink";

const IconBadge = ({ children }: { children: React.ReactNode }) => (
  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-95 text-primary-50 dark:bg-primary-30 dark:text-primary-80">
    {children}
  </span>
);

const Card = ({ children }: { children: React.ReactNode }) => (
  <div className="with-border rounded-2xl p-4 lg:p-6 transition-colors hover:border-primary-50/40 dark:hover:border-primary-70/40">
    {children}
  </div>
);

const CardHeader = ({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
}) => (
  <div className="mb-4 flex items-start gap-3">
    <IconBadge>{icon}</IconBadge>
    <div className="min-w-0">
      <h2 className="text-lg font-semibold leading-tight">{title}</h2>
      {description && (
        <p className="text-sm text-grey-50 dark:text-grey-70 mt-0.5">{description}</p>
      )}
    </div>
  </div>
);


const SettingsPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentRegionalCode = useAppSelector(selectCurrentRegionalCode);
  const availableVariants = useAppSelector(selectAvailableVariants);
  const { themeMode, changeTheme } = useTheme();

  const handleLanguageChange = (variantCode: string) => {
    dispatch(changeLanguage(variantCode));
  };

  return (
    <div className="flex h-full gap-4">
      <LeftMenu />
      <div className="w-full p-4 pb-10 lg:px-10">
        <div className="lg:max-w-[960px]">
          <Breadcrumbs />
          <div className="py-6">
            <h1>{t("settings.title")}</h1>

            {/* Top row: Account / Appearance / Language — small cards share one row.
                Tighter spacing inside the row, larger gap below to group them visually. */}
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6 lg:mt-6">
              <Card>
                <CardHeader
                  icon={<User size={20} strokeWidth={2} />}
                  title={t("settings.account")}
                  description={t("settings.signedInUser")}
                />
                <ProfileNameEditor />
                <LicenceNumberEditor />
              </Card>

              <Card>
                <CardHeader
                  icon={<Palette size={20} strokeWidth={2} />}
                  title={t("settings.appearance")}
                  description={t("settings.appearanceDescription")}
                />
                <SegmentedToggle<ThemeMode>
                  value={themeMode}
                  onChange={changeTheme}
                  fullWidth
                  options={[
                    { value: "light", label: t("settings.themeLight"), icon: <Sun size={16} /> },
                    { value: "auto", label: t("settings.themeAuto"), icon: <Monitor size={16} /> },
                    { value: "dark", label: t("settings.themeDark"), icon: <MoonStar size={16} /> },
                  ]}
                />
              </Card>

              <Card>
                <CardHeader
                  icon={<Globe size={20} strokeWidth={2} />}
                  title={t("settings.language")}
                  description={t("settings.languageDescription")}
                />
                <select
                  className="input w-full"
                  value={currentRegionalCode}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                >
                  {availableVariants.map((variantCode) => {
                    const variant = REGIONAL_VARIANTS[variantCode];
                    if (!variant) return null;
                    return (
                      <option key={variantCode} value={variantCode}>
                        {variant.nativeName}
                      </option>
                    );
                  })}
                </select>
              </Card>

              {/* Organization — only rendered for Owner/Admin */}
              <OrganizationSettingsLink />

              <ProgrammesSettingsLink />
            </div>

            {/* Hero: Working hours — separated from the row above by a larger gap */}
            <div className="mt-8 lg:mt-10">
              <Card>
                <CardHeader
                  icon={<Clock size={20} strokeWidth={2} />}
                  title={t("settings.workingHoursTitle")}
                  description={t("settings.workingHoursDescription")}
                />
                <SchedulePreferencesSection hideHeader />
              </Card>
            </div>

            {/* Legal: the documents this instructor has accepted, with dates */}
            <div className="mt-8 lg:mt-10">
              <LegalConsentSettings />
            </div>

            {/* Danger zone: Owner-only account deletion (14-day grace window) */}
            <div className="mt-8 lg:mt-10">
              <DeleteAccountSection />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
